import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface StoryBulletin {
  id: string;
  content: string;
  image_url: string | null;
  thumbnail_url: string | null;
  created_at: string;
  user_id: string;
  profiles?: {
    username: string;
    display_name: string | null;
    profile_image_url: string | null;
    is_admin: boolean | null;
  };
}

export interface UserStories {
  user_id: string;
  profile: {
    username: string;
    display_name: string | null;
    profile_image_url: string | null;
    is_admin: boolean | null;
  };
  bulletins: StoryBulletin[];
  unread_count: number;
}

export const useFollowFeedStories = (favoriteUserId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['follow-feed-stories', user?.id, favoriteUserId],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get user's favorites
      const { data: favorites, error: favError } = await supabase
        .from('user_favorites')
        .select('favorite_user_id')
        .eq('user_id', user.id);

      if (favError) throw favError;

      const favoriteUserIds = favorites?.map(f => f.favorite_user_id) || [];
      
      if (favoriteUserIds.length === 0) return [];

      // If specific user requested, filter to that user
      const targetUserIds = favoriteUserId ? [favoriteUserId] : favoriteUserIds;

      // Get recent bulletins from favorite users (last 24 hours)
      const { data: bulletins, error: bulletinError } = await supabase
        .from('bulletins')
        .select(`
          id,
          content,
          image_url,
          thumbnail_url,
          created_at,
          user_id,
          profiles:user_id (
            username,
            display_name,
            profile_image_url,
            is_admin
          )
        `)
        .in('user_id', targetUserIds)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false });

      if (bulletinError) throw bulletinError;

      // Get viewed stories
      const { data: viewedStories } = await supabase
        .from('user_story_views')
        .select('bulletin_id')
        .eq('user_id', user.id)
        .eq('story_context', 'follow_feed');

      const viewedBulletinIds = new Set(viewedStories?.map(v => v.bulletin_id) || []);

      // Group bulletins by user and calculate unread counts
      const userStoriesMap = new Map<string, UserStories>();
      
      bulletins?.forEach((bulletin: any) => {
        const profile = Array.isArray(bulletin.profiles) ? bulletin.profiles[0] : bulletin.profiles;
        
        if (!userStoriesMap.has(bulletin.user_id)) {
          userStoriesMap.set(bulletin.user_id, {
            user_id: bulletin.user_id,
            profile,
            bulletins: [],
            unread_count: 0
          });
        }

        const userStory = userStoriesMap.get(bulletin.user_id)!;
        userStory.bulletins.push(bulletin);
        
        if (!viewedBulletinIds.has(bulletin.id)) {
          userStory.unread_count++;
        }
      });

      // Convert map to array and sort by unread count (unread first)
      const userStoriesArray = Array.from(userStoriesMap.values())
        .sort((a, b) => b.unread_count - a.unread_count);

      return userStoriesArray;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useMarkStoryAsViewed = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bulletinId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_story_views')
        .insert({
          user_id: user.id,
          bulletin_id: bulletinId,
          story_context: 'follow_feed'
        });

      if (error && !error.message.includes('duplicate key')) {
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate follow feed stories to update unread counts
      queryClient.invalidateQueries({ queryKey: ['follow-feed-stories'] });
      queryClient.invalidateQueries({ queryKey: ['favorite-users'] });
    },
  });
};